/**
 * Governance Suite Router Configuration
 *
 * All routes except /login are protected by authentication.
 *
 * @module router
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@hello-world-dao/auth';
import { MembershipRequired } from './components/auth/MembershipRequired';
import { GovernanceLayout } from './pages/Governance';
import { DiscussionsPage } from './pages/Governance/DiscussionsPage';
import { DiscussionDetailPage } from './pages/Governance/DiscussionDetailPage';
import { ProposalsPage } from './pages/Governance/ProposalsPage';
import { ProposalDetailPage } from './pages/Governance/ProposalDetailPage';
import { MyVotesPage } from './pages/Governance/MyVotesPage';
import { VotingPage } from './pages/Governance/VotingPage';
import { LoginFrontdoorRedirect } from './pages/Login';

/**
 * Application router with all governance routes
 */
export const router = createBrowserRouter([
  // Public route - redirects to the identity-service login front-door (dac-001-4d-2).
  // The in-suite v1 email/password form + legacy II login button were retired here;
  // the hosted front-door authenticates the member and returns via `?code=`.
  {
    path: '/login',
    element: <LoginFrontdoorRedirect />,
  },
  // Protected routes - require authentication
  {
    path: '/',
    element: (
      <ProtectedRoute
        requiredRole="member"
        unauthorizedComponent={<MembershipRequired />}
        loginUrl="/login"
        redirectBehavior="local"
      >
        <GovernanceLayout />
      </ProtectedRoute>
    ),
    children: [
      // Default redirect to discussions
      {
        index: true,
        element: <Navigate to="/discussions" replace />,
      },
      // Discussions routes
      {
        path: 'discussions',
        element: <DiscussionsPage />,
      },
      {
        path: 'discussions/:discussionId',
        element: <DiscussionDetailPage />,
      },
      // Proposals routes
      {
        path: 'proposals',
        element: <ProposalsPage />,
      },
      {
        path: 'proposals/:proposalId',
        element: <ProposalDetailPage />,
      },
      // My Votes route
      {
        path: 'my-votes',
        element: <MyVotesPage />,
      },
      // Voting routes
      {
        path: 'voting',
        element: <VotingPage />,
      },
      {
        path: 'voting/:proposalId',
        element: <VotingPage />,
      },
    ],
  },
]);

export default router;
